import { Product } from "@/types"
import { SerpElement } from "@nosto/search-js/preact/serp"
import styles from "./Product.module.css"
import skeleton from "./skeleton.module.css"
import "./DynamicCardProduct.css"
import DynamicCard from "@/elements/DynamicCard/DynamicCard"
import { cl } from "@nosto/search-js/utils"
import { getVariantId } from "@/utils/getVariantId"

type Props = {
  product: Product
  /** Position within the product sequence (in-grid tiles excluded). Drives LCP section selection. */
  productIndex?: number
}

const DEFAULT_SECTION = "nosto-dickies-product-card"
const LCP_SECTION = "nosto-dickies-product-card-lcp"
const EAGER_SECTION = "nosto-dickies-product-card-eager"
const EAGER_COUNT = 4

/**
 * LCP fix: the first product row sits above the fold, but Liquid
 * renders each card in isolation via the Section Rendering API and cannot
 * know grid position — so position is encoded in the section name. Index 0
 * (the page's LCP candidate) fetches the eager + fetchpriority=high variant,
 * indexes 1-3 the eager variant, everything else the default lazy section.
 * All three sections render the same `dickies-product-card` snippet and
 * differ only in the primary image's loading attributes.
 */
function sectionForIndex(index?: number) {
  if (index === 0) return LCP_SECTION
  if (index !== undefined && index > 0 && index < EAGER_COUNT) return EAGER_SECTION
  return DEFAULT_SECTION
}

/**
 * PLP / category / search-result grid card.
 *
 * Renders the Dickies product card by fetching the Shopify-rendered section
 * `nosto-dickies-product-card` via Nosto's `<nosto-dynamic-card>` web component,
 * so all family-swatch, PFAS, badge, price, clearance, discount-message, and
 * quick-view behaviour stays owned by Liquid (see Agent C's theme foundation).
 *
 * The skeleton path (driven by `skeletonContent` putting `tags1: ["skeleton"]`
 * on placeholder hits) is preserved so the initial pre-search layout still
 * shows a skeleton without trying to fetch an unknown product handle.
 */
export default function DynamicCardProduct({ product, productIndex }: Props) {
  const isSkeleton = product.tags1?.includes("skeleton")

  if (isSkeleton) {
    return (
      <div className={cl(styles.container, skeleton.skeleton)} aria-label={`Product ${product.name}`}>
        <div className={styles.image}>
          <img src={product.imageUrl} alt={product.name} className={styles.img} />
        </div>
        <div className={styles.info}>
          <div className={skeleton.text}>...</div>
          <div className={skeleton.text}>...</div>
          <div className={skeleton.text}>...</div>
        </div>
      </div>
    )
  }

  // Without a handle the section URL `/products/{handle}?section_id=...` cannot
  // be built, so skip rendering instead of issuing a guaranteed-404 fetch.
  if (!product.handle) {
    return null
  }

  const variantId = getVariantId(product)

  return (
    <SerpElement
      as={DynamicCard}
      hit={{
        productId: product.productId!,
        url: product.url
      }}
      componentProps={{
        handle: product.handle,
        section: sectionForIndex(productIndex),
        placeholder: true,
        lazy: false,
        className: styles.container,
        ...(variantId ? { "variant-id": variantId } : {})
      }}
    />
  )
}
